import { CircularProgress } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import clsx from 'clsx'
import React from 'react'
import useStyles from './CopyMoveModal.styles'
export interface CopyMoveModalStypeProps {
    /**
     * Whethere to remove borders from top and bottom of footer and header respectively
     */
    removeHeaderBorder?: boolean
    removeFooterBorder?: boolean
    /**
     * To determine color of title in header
     */
    headerColor?: string
    isForAssociation?: boolean
}

export interface CopyMoveModalProps extends CopyMoveModalStypeProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose?: VoidFunction

    /**
     * Trigger action on confirmation click
     */
    dialogActionCallback: (_: React.MouseEvent<Element, MouseEvent>) => void
    /**
     * Defines the heading of the confirmation dialog. Default: Confirm
     */
    dialogHeading?: string

    /**
     * Defines the content for the confirmation dialog.
     */
    contentNode?: React.ReactNode

    /*
     * Defines the confirmation message placed above action buttons on the confirmation dialog. Default: Are you sure you want to proceed?
     */
    confirmMessage?: string

    /*
     * Defines the text for cancel action button on confirmation dialog. Default: Cancel
     */
    cancelText?: string

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: Ok
     */
    confirmText?: string

    /**
     * Defines the size for the confirmation dialog.
     */
    size?: 'small' | 'default' | 'large'
    /**
     * Top and bottom spacing for  title text on header
     */
    titlePadding?: boolean
    /**
     * Top  spacing for  main content item
     */
    /**
     * For setting overflow in dialog of large size
     */
    contentOverflow?: boolean
    /**
     * For setting overflow in dialog of large size
     */
    calledForImportExecution?: boolean
    loading?: boolean
    dialogActionCancelCallback?: VoidFunction
    disabled?: boolean
    auditDetails?: AuditType
    isBulkAction?: boolean
}

interface AuditType {
    sourceProductCount?: number
    matchPropertiesCount?: number
}

const CopyMoveModal: React.FC<CopyMoveModalProps> = ({
    openDialog,
    handleDialogClose,
    dialogActionCallback,
    dialogActionCancelCallback,
    dialogHeading = 'Move or copy a product',
    contentNode,
    confirmMessage = '',
    cancelText = 'Copy',
    confirmText = 'Move',
    size,
    removeHeaderBorder,
    removeFooterBorder,
    headerColor = '#EB5757',
    titlePadding,
    isForAssociation,
    contentOverflow,
    calledForImportExecution,
    loading,
    disabled,
    auditDetails,
    isBulkAction,
}) => {
    const classes = useStyles({
        removeFooterBorder,
        removeHeaderBorder,
        headerColor,
        isForAssociation,
    })

    return (
        <Dialog
            open={openDialog}
            data-testid="projectRemovalWarning"
            className={classes.warningDialogCont}
            aria-labelledby="remove-project-properties"
            maxWidth="lg"
        >
            <DialogTitle
                className={clsx(
                    classes.dialogTitle,
                    titlePadding && classes.dialogTitlePadding,
                    isForAssociation && classes.forAssociation
                )}
            >
                {dialogHeading}
                <IconButton onClick={handleDialogClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider variant="fullWidth" />

            <DialogContent
                className={clsx(
                    classes.dialogCont,
                    contentOverflow && classes.addContentOverflow,
                    size === 'small' && classes.dialogContSmall,
                    size === 'default' && classes.dialogContDefault,
                    classes.marginAuto
                )}
            >
                {contentNode}
            </DialogContent>
            {!removeFooterBorder && <Divider variant="fullWidth" />}
            <Typography
                className={clsx(
                    confirmMessage?.length !== 0 && classes.confirmationText
                )}
            >
                {confirmMessage}
            </Typography>
            <DialogActions className={classes.dialogAction}>
                {!!auditDetails ? (
                    <div data-testid="auditDataText">
                        <Typography className={classes.detailText}>
                            {auditDetails.matchPropertiesCount || 0} of{' '}
                            {auditDetails.sourceProductCount || 0} properties
                            and their options will automatically transfer during
                            the move or copy.
                        </Typography>
                        <Typography className={classes.detailText}>
                            {auditDetails.matchPropertiesCount || 0} = Number of
                            product type properties that have a selected option
                            in the source product and at least <br /> one match
                            with the options in the destination product type.
                        </Typography>
                        <Typography className={classes.detailText}>
                            {auditDetails.sourceProductCount || 0} = Number of
                            product type properties in the source product.
                        </Typography>
                    </div>
                ) : (
                    <></>
                )}
                <Button
                    data-testid="cancelRemoveButton"
                    className={clsx({
                        [classes.actionButton]: !isBulkAction,
                        [classes.actionButtonBulk]: isBulkAction,
                    })}
                    onClick={dialogActionCancelCallback}
                    disabled={disabled || loading}
                >
                    {cancelText}
                </Button>
                <Button
                    data-testid="confirmRemoveButton"
                    onClick={dialogActionCallback}
                    disabled={disabled || loading}
                    className={
                        calledForImportExecution
                            ? classes.actionButtonImportOnly
                            : classes.actionButton
                    }
                >
                    {confirmText}
                </Button>
            </DialogActions>
            {loading && (
                <CircularProgress className={classes.circularProgressCenter} />
            )}
        </Dialog>
    )
}

export default CopyMoveModal
